/**
 * metaConversionsHelper.js
 *
 * Low-level Meta tracking engine.
 * Import through metaPageTracking.js — NOT directly from pages.
 *
 * Responsibilities:
 *   • Pixel initialisation (once; iOS-safe)
 *   • Browser-side event firing via fbq()
 *   • Server-side Conversions API call → POST /api/orders/meta-conversions
 */

import { getCookie, getFacebookBrowserId } from './fbclid';

// ─── Internal helpers ─────────────────────────────────────────────────────────

const isIOS = () => /iphone|ipad|ipod/i.test(navigator.userAgent);

const generateExternalId = () => {
  try {
    const stored = localStorage.getItem('_fb_ext_id');
    if (stored) return stored;
    const id = `ext_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    localStorage.setItem('_fb_ext_id', id);
    return id;
  } catch {
    return `ext_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }
};

let _clientIP = null;
const getClientIP = async () => {
  if (_clientIP) return _clientIP;
  try {
    const res    = await fetch('https://api.ipify.org?format=json');
    const { ip } = await res.json();
    _clientIP = ip;
    return ip;
  } catch {
    return null;
  }
};

/**
 * Inject the pixel script dynamically — needed on iOS where the base snippet
 * sometimes fails to fire before the page becomes interactive.
 */
const _applyIOSEnhancement = (pixelId) => {
  if (document.querySelector('script[src*="fbevents.js"]')) return;

  // The fbq stub MUST exist before fbevents.js loads — the script references
  // `fbq` as a bare global on execution and throws ReferenceError without it.
  /* eslint-disable */
  if (!window.fbq) {
    var n = window.fbq = function() {
      n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
    };
    if (!window._fbq) window._fbq = n;
    n.push = n; n.loaded = true; n.version = '2.0'; n.queue = [];
  }
  /* eslint-enable */

  window.fbq('init', pixelId);
  window.fbq('track', 'PageView');

  const s    = document.createElement('script');
  s.async    = true;
  s.src      = 'https://connect.facebook.net/en_US/fbevents.js';
  (document.head || document.body).appendChild(s);
};

// ─── Public API ───────────────────────────────────────────────────────────────

let _pixelInitialised = false;

/**
 * Initialise the Facebook Pixel. Safe to call multiple times — runs only once.
 * Handles iOS quirks automatically.
 * @param {string} pixelId
 */
export const initPixel = (pixelId) => {
  if (!pixelId || _pixelInitialised || typeof window === 'undefined') return;

  if (isIOS() && !window.fbq) {
    _applyIOSEnhancement(pixelId);
    _pixelInitialised = true;
    return;
  }

  // Standard Meta base-code snippet (ESLint comment required to avoid no-unused-expressions)
  /* eslint-disable */
  !function(f,b,e,v,n,t,s){
    if(f.fbq)return;n=f.fbq=function(){n.callMethod?
    n.callMethod.apply(n,arguments):n.queue.push(arguments)};
    if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
    n.queue=[];t=b.createElement(e);t.async=!0;
    t.src=v;s=b.getElementsByTagName(e)[0];
    s.parentNode.insertBefore(t,s)}(window,document,'script',
    'https://connect.facebook.net/en_US/fbevents.js');
  /* eslint-enable */

  window.fbq('init', pixelId);
  _pixelInitialised = true;
};

/**
 * Fire a browser-side Pixel event.
 * @param {string}  eventName  Standard Meta event name, e.g. 'PageView', 'Lead', 'Purchase'
 * @param {object}  [data]     Optional event parameters
 * @param {string}  [eventId]  Deduplication ID — pass the same ID to sendCAPIEvent()
 * @returns {boolean} Whether the event was fired
 */
export const firePixelEvent = (eventName, data = {}, eventId) => {
  if (typeof window === 'undefined' || !window.fbq) return false;
  if (eventId) {
    window.fbq('track', eventName, data, { eventID: eventId });
  } else {
    window.fbq('track', eventName, data);
  }
  return true;
};

/**
 * Send an event to your backend's Meta Conversions API endpoint.
 * Fires the matching browser Pixel event first (for deduplication).
 *
 * @param {string} eventName   e.g. 'Purchase'
 * @param {{ orderId, amount, currency?, userId?, customerEmail?, customerPhone? }} orderData
 * @returns {Promise<{ success: boolean, data?: object, error?: any }>}
 */
export const sendCAPIEvent = async (eventName, orderData) => {
  const {
    orderId,
    amount,
    currency      = 'INR',
    userId        = null,
    customerEmail = null,
    customerPhone = null,
  } = orderData;

  const eventId  = `${eventName}_${orderId}_${Date.now()}`;
  const clientIP = await getClientIP();

  // Browser-side Pixel event (dedup ID links it to the CAPI event below)
  firePixelEvent(eventName, { value: parseFloat(amount || 0), currency }, eventId);

  const payload = {
    orderId,
    amount:                parseFloat(amount || 0),
    currency,
    userId,
    customerEmail,
    customerPhone,
    eventId,
    eventTime:             new Date().toISOString(),
    eventTimeUnix:         Math.floor(Date.now() / 1000),
    eventSourceUrl:        window.location.href,
    clientIpFromFrontend:  clientIP,
    userAgentFromFrontend: navigator.userAgent,
    fbp:                   getCookie('_fbp') || getFacebookBrowserId(),
    fbc:                   getCookie('_fbc') || localStorage.getItem('_fbc_fallback'),
    externalId:            generateExternalId(),
    cookiesEnabled:        navigator.cookieEnabled,
    visitor_id:            localStorage.getItem('_lv_visitor_id') || null,
  };

  try {
    const res    = await fetch('/api/orders/meta-conversions', {
      method:  'POST',
      headers: { 'Content-Type': 'application/json' },
      body:    JSON.stringify(payload),
    });
    const result = await res.json();
    return result.status === 'success'
      ? { success: true,  data: result }
      : { success: false, error: result };
  } catch (err) {
    console.warn('[MetaConversions] CAPI call failed (non-blocking):', err);
    return { success: false, error: err.message };
  }
};
